import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { DialogResponse, DialogResponseCode, DialogType, modeUIContext, showDialog } from '@moderepo/mode-ui-state';
import { capitalizeAll, GenericMessageBox } from '@moderepo/mode-app-components';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { RequestMethod, ExternalAPI } from '@moderepo/mode-apis';
import { Grid, makeStyles, Theme } from '@material-ui/core';
import { TSDownloadDataRequestsTable, TSDownDataRequestsTableDataItem, TSDownloadDataRequestsTableProps } from '../../components';
import { useRitzTranslation } from '../../utils/customHooks';
import { DownloadableDataInfo, DownloadDataRequestInfo, DownloadDataRequestStatus, TSDownloadDataRequestManager }
    from './TSDownloadDataRequestManager';
import { getDateTimeDisplay } from '../containerUtils';


const useStyle = makeStyles((theme: Theme) => {
    return {
        note: {
            color: theme.palette.text.primary,
        },

        instructions: {
            display      : 'flex',
            flexDirection: 'column',
            verticalAlign: 'center',
        },

        instruction: {
            display      : 'flex',
            flexDirection: 'row',
            verticalAlign: 'center',
            margin       : theme.spacing(0.5, 0),
            '& .icon'    : {
                marginRight: theme.spacing(1),
            },
        },
    };
});


/**
 * User friendly name for TS data export request statuses. These will be used for displaying the status names.
 */
export const getDataExportRequestStatusName = (status: string): string => {
    const RequestStatusName = Object.freeze({
        [DownloadDataRequestStatus.PENDING]: 'ts_download_request.status.pending',
        [DownloadDataRequestStatus.READY]  : 'ts_download_request.status.ready',
        [DownloadDataRequestStatus.EXPIRED]: 'ts_download_request.status.expired',
    });
    return RequestStatusName[status];
};


// Check request status every 10 seconds
const DATA_REQUEST_STATUS_CHECK_FREQ = 10000;

export interface TSDownloadDataRequestsListingContainerProps {
    readonly projectId: number;
}

export const TSDownloadDataRequestsListingContainer: React.FC<TSDownloadDataRequestsListingContainerProps> = (
    props: TSDownloadDataRequestsListingContainerProps,
) => {
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState } = useContext(ritzDataContext);
    const project = selectProjectById(dataState, props.projectId);
    const { trans } = useRitzTranslation(project);

    const classes = useStyle();

    const tsDownloadDataRequestManager = useMemo(() => {
        return TSDownloadDataRequestManager.getInstance();
    }, []);

    const [dataRequests, setDataRequests] = useState<readonly DownloadDataRequestInfo[]>([]);

    // The timerId of the interval we run to check request status automatically
    const dataRequestStatusCheckTimerId = useRef<number | undefined>();


    // Sort the dataRequests by creationTime and have the most recent created request be first
    const sortedDataRequests = useMemo(() => {
        return [...dataRequests].sort((request1: DownloadDataRequestInfo, request2: DownloadDataRequestInfo) => {
            const timestamp1 = (Date.parse(request1.creationTime)).valueOf();
            const timestamp2 = (Date.parse(request2.creationTime)).valueOf();
            return timestamp2 - timestamp1;
        });
    }, [dataRequests]);


    const localizedDataRequests = useMemo(() => {
        if (sortedDataRequests) {
            return sortedDataRequests.map((dataRequest: DownloadDataRequestInfo): TSDownDataRequestsTableDataItem => {
                return {
                    actualValue : dataRequest,
                    displayValue: {
                        id      : dataRequest.id.toString(),
                        dataInfo: dataRequest.dataInfo.map((dataInfo: DownloadableDataInfo): string => {
                            return dataInfo.name || dataInfo.id;
                        }).join(', '),
                        status        : capitalizeAll(trans(getDataExportRequestStatusName(dataRequest.status))),
                        downloadLink  : dataRequest.requestResponse.dataUrl,
                        startDate     : getDateTimeDisplay(dataRequest.dataRange.start, trans),
                        endDate       : getDateTimeDisplay(dataRequest.dataRange.end, trans),
                        creationTime  : getDateTimeDisplay(dataRequest.creationTime, trans),
                        expirationTime: getDateTimeDisplay(dataRequest.expirationTime, trans),
                    },
                    canBeRemoved: true,
                };
            });
        }
        return undefined;
    }, [sortedDataRequests, trans]);


    const checkDataRequestsStatus = useCallback(async () => {
        const requests = tsDownloadDataRequestManager.getRequests(props.projectId);

        // Get the list of requests that require status check which are PENDING requests
        const requestsRequireStatusCheck = requests.filter((request: DownloadDataRequestInfo) => {
            return request.status === DownloadDataRequestStatus.PENDING;
        });

        const statusCheckApiCalls = requestsRequireStatusCheck.map((request: DownloadDataRequestInfo) => {
            return ExternalAPI.getInstance().sendRequest(RequestMethod.GET, request.requestResponse.statusUrl);
        });

        await Promise.allSettled(statusCheckApiCalls)
            // When all the API calls are done
            .then((allResults) => {

                // NOTE: allResults might include failed API call for data requests which are not ready or are expired.
                // For each 'fulfilled' and if response status is 'SUCCESS', we will update the data request's status
                allResults.forEach((singleResult, index: number) => {
                    if (singleResult.status === 'fulfilled' && singleResult.value.data.status === 'SUCCESS') {
                        // API call success and the response's status is 'SUCCESS' mean the data URL is ready
                        tsDownloadDataRequestManager.markRequestReady(props.projectId, requestsRequireStatusCheck[index].id);
                    } else if (
                        singleResult.status === 'rejected' && singleResult.reason.status === 400 && singleResult.reason.code === 'NETWORK_ERROR'
                    ) {
                        // API call failed and the response reason's status is 400 and code contains the word 'NETWORK_ERROR' means
                        // CORS blocked so we will assume the data is ready because we will never be able to tell if data is ready
                        tsDownloadDataRequestManager.markRequestReady(props.projectId, requestsRequireStatusCheck[index].id);
                    } else if (
                        singleResult.status === 'rejected' && singleResult.reason.status === 400 && singleResult.reason.code.includes('UNKNOWN')
                    ) {
                        // API call failed and the response reason's status is 400 and code contains the word 'UNKNOWN' then URL is expired
                        tsDownloadDataRequestManager.markRequestExpired(props.projectId, requestsRequireStatusCheck[index].id);
                    }
                });

                setDataRequests(tsDownloadDataRequestManager.getRequests(props.projectId));
            });
    }, [props.projectId, tsDownloadDataRequestManager]);



    /**
     * This useEffect is used for loading the current list of data request on component init.
     * This will also create an interval to keep checking the status of the PENDING requests and will automatically update those requests'
     * status when the status changed to READY.
     */
    useEffect(() => {
        // Clear the previous interval if there is one
        if (dataRequestStatusCheckTimerId) {
            window.clearInterval(dataRequestStatusCheckTimerId.current);
        }

        dataRequestStatusCheckTimerId.current = window.setInterval(() => {
            checkDataRequestsStatus();
        }, DATA_REQUEST_STATUS_CHECK_FREQ);

        checkDataRequestsStatus();

        return () => {
            // clean up
            window.clearInterval(dataRequestStatusCheckTimerId.current);
            dataRequestStatusCheckTimerId.current = undefined;
        };
    }, [checkDataRequestsStatus]);


    const fieldSettings = useMemo((): TSDownloadDataRequestsTableProps['fieldsSettings'] => {
        return {
            fields: {
                creationTime: {
                    label       : capitalizeAll(trans('label.requested_date')),
                    dataItemProp: 'creationTime',
                },
                id: {
                    label       : capitalizeAll(trans('label.id')),
                    dataItemProp: 'id',
                    hidden      : true,
                },
                dataInfo: {
                    label       : capitalizeAll(trans('label.selected_data')),
                    dataItemProp: 'dataInfo',
                },
                startDate: {
                    label       : capitalizeAll(trans('label.data_start_date')),
                    dataItemProp: 'startDate',
                },
                endDate: {
                    label       : capitalizeAll(trans('label.data_end_date')),
                    dataItemProp: 'dataRange',
                },
                status: {
                    label       : capitalizeAll(trans('label.status')),
                    dataItemProp: 'status',
                },
                downloadLink: {
                    label       : '',
                    dataItemProp: 'download',
                },
                expirationTime: {
                    label       : capitalizeAll(trans('label.download_expiration_date')),
                    dataItemProp: 'expirationTime',
                },
                remove: {
                    label: '',
                },
            },
        };
    }, [trans]);


    /**
     * On check request status. If status return "SUCCESS", update the request's status
     */
    const onCheckRequestStatusClick = useCallback(async (request: DownloadDataRequestInfo) => {
        try {
            const response = await ExternalAPI.getInstance().sendRequest(RequestMethod.GET, request.requestResponse.statusUrl);
            if (response.data.status === 'SUCCESS') {
                tsDownloadDataRequestManager.markRequestReady(props.projectId, request.id);
                setDataRequests(tsDownloadDataRequestManager.getRequests(props.projectId));
            }
        } catch (error) {
            // if there is error, it is either the status is NOT ready or the request expired
        }
    }, [props.projectId, tsDownloadDataRequestManager]);


    /**
     * On remove request click handler.
     */
    const onRemoveRequestClicked = useCallback(async (request: DownloadDataRequestInfo) => {
        uiDispatch(showDialog({
            type          : DialogType.CONFIRM,
            title         : 'dialog.confirm_remove_ts_download_request.title',
            message       : 'dialog.confirm_remove_ts_download_request.message',
            positiveButton: 'label.remove',
            negativeButton: 'label.cancel',
            onClose       : async (response?: DialogResponse): Promise<void> => {
                if (response && response.code === DialogResponseCode.POSITIVE) {
                    tsDownloadDataRequestManager.removeRequest(props.projectId, request.id);
                    setDataRequests(tsDownloadDataRequestManager.getRequests(props.projectId));
                }
            },
        }));
    }, [props.projectId, tsDownloadDataRequestManager, uiDispatch]);


    return (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <div className={classes.note}>
                    {trans('panel.ts_download_request_view.message')}
                </div>
            </Grid>
            <Grid item xs={12}>
                <TSDownloadDataRequestsTable
                    title={capitalizeAll(trans('panel.ts_download_request_view.title'))}
                    icon="get_app"
                    fieldsSettings={fieldSettings}
                    listData={localizedDataRequests}
                    texts={{
                        checkStatusTooltip: capitalizeAll(trans('tooltip.ts_download_request_view.check_status')),
                        downloadTooltip   : capitalizeAll(trans('tooltip.ts_download_request_view.download')),
                        expiredTooltip    : capitalizeAll(trans('tooltip.ts_download_request_view.expired')),
                    }}
                    noDataMessageComp={(
                        <GenericMessageBox
                            title={trans('generic.no_data.title', {
                                term: capitalizeAll(trans('label.ts_download_request', { count: 2 })),
                            })}
                        />
                    )}
                    emptyDataMessageComp={(
                        <GenericMessageBox
                            title={trans('generic.no_data.title', {
                                term: capitalizeAll(trans('label.ts_download_request', { count: 2 })),
                            })}
                        />
                    )}
                    onCheckRequestStatusClick={onCheckRequestStatusClick}
                    onRemoveItemClicked={onRemoveRequestClicked}
                />
            </Grid>
        </Grid>
    );
};
